/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";

interface SidebarProps {
  isLeftBarActive: boolean;
  handleMenuItemClick: (e: any) => void;
  getIsCoupon: boolean;
  getIsReport: boolean;
  handleNavigation: (hasAccess: boolean, path: string) => void;
  ProfileDetails: (chefId: string) => Promise<any>;
}

const Sidebar: React.FC<SidebarProps> = ({
  isLeftBarActive,
  handleMenuItemClick,
  getIsCoupon,
  getIsReport,
  handleNavigation,
  ProfileDetails,
}) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const chefId = currentUser?.uuid || "";
  const [slug, setSlug] = useState<any>("");
  const location = useLocation(); // React Router's hook to track the current location

  useEffect(() => {
    const firstSlug = location.pathname.split("/").find(Boolean) || "";
    setSlug(firstSlug);
  }, [location.pathname]);

  return (
    <section className={`left-bar ${isLeftBarActive ? "active" : ""}`}>
      <div className="lb-top">
        <div className="nav-brand mx-auto mt-md-4 mt-4 mb-4">
          <Link
            className={`nav-link ${slug === "dashboard" ? "active" : ""}`}
            to="/dashboard"
            onClick={handleMenuItemClick}
          >
            <img
              className="img-fluid"
              src="/assets/images/logo.svg"
              alt="Frayo"
            />
          </Link>
        </div>
        <ul className="nav flex-column">
          <li className="nav-item">
            <Link
              className={`nav-link ${slug === "dashboard" ? "active" : ""}`}
              to="/dashboard"
              onClick={handleMenuItemClick}
            >
              <i className="material-symbols-outlined">home</i>
              <span>Home</span>
            </Link>
          </li>
          <li className="nav-item">
            <Link
              className={`nav-link ${slug === "orders" ? "active" : ""}`}
              to="/orders"
              onClick={(e) => {
                handleMenuItemClick(e);
                ProfileDetails(chefId).then((res) => {
                  const chefStatus = res?.data?.currentlyAvailableStatus;
                  if (chefStatus === true) {
                    navigate("/orders/success");
                  } else if (chefStatus === false) {
                    navigate("/orders/list");
                  }
                });
              }}
            >
              <i className="material-symbols-outlined">orders</i>
              <span>Orders</span>
            </Link>
          </li>

          <li className="nav-item">
            <Link
              className={`nav-link ${slug === "dish" ? "active" : ""}`}
              to="/dish"
              onClick={handleMenuItemClick}
            >
              <i className="material-symbols-outlined">restaurant</i>
              <span>Dishes</span>
            </Link>
            <ul className="sub-menu ps-3 ">
              <li className="nav-item">
                <Link
                  className={`nav-link ${slug === "addons" ? "active" : ""}`}
                  to="/addons"
                  onClick={handleMenuItemClick}
                >
                  <i className="material-symbols-outlined">add_circle</i>
                  <span>Addons</span>
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  className={`nav-link ${slug === "ingredients" ? "active" : ""
                    }`}
                  to="/ingredients"
                  onClick={handleMenuItemClick}
                >
                  <i className="material-symbols-outlined">kitchen</i>
                  <span>Ingredients</span>
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  className={`nav-link ${slug === "dish-category" ? "active" : ""
                    }`}
                  to="/dish-category"
                  onClick={handleMenuItemClick}
                >
                  <i className="material-symbols-outlined">category</i>
                  <span>Category</span>
                </Link>
              </li>

              {/* <li className="nav-item">
                <Link
                  className={`nav-link ${slug === "dish-review" ? "active" : ""
                    }`}
                  to="/dish-review"
                  onClick={handleMenuItemClick}
                >
                  <i className="material-symbols-outlined">reviews</i>
                  <span>Reviews</span>
                </Link>
              </li> */}
            </ul>
          </li>
          <li className="nav-item">
            <Link
              className={`nav-link ${slug === "settlements" ? "active" : ""}`}
              to="/settlements"
              onClick={handleMenuItemClick}
            >
              <i className="material-symbols-outlined">payments</i>
              <span>Settlements</span>
            </Link>
          </li>
          <li className="nav-item">
            <Link
              className={`nav-link ${slug === "coupon" ? "active" : ""}`}
              to={getIsCoupon ? "/coupon" : "#"} // Navigate to "/coupon" if access is granted, else "#"
              onClick={(e) => {
                if (!getIsCoupon) {
                  e.preventDefault(); // Prevent default behavior if no access
                  handleNavigation(getIsCoupon, "/coupon");
                } else {
                  navigate("/coupon"); // Navigate if access is granted
                }
              }}
            >
              <i className="material-symbols-outlined">sell</i>
              <span>Coupons</span>
            </Link>
          </li>
          <li className="nav-item">
            <Link
              className={`nav-link ${slug === "report" ? "active" : ""}`}
              to={getIsReport ? "/report" : "#"} // Navigate to "/report" if access is granted, else "#"
              onClick={(e) => {
                if (!getIsReport) {
                  e.preventDefault(); // Prevent default behavior if no access
                  handleNavigation(getIsReport, "/report");
                } else {
                  navigate("/report"); // Navigate if access is granted
                }
              }}
            >
              <i className="material-symbols-outlined">analytics</i>
              <span>Reports</span>
            </Link>
          </li>
          {/* Logging access rights for debugging */}
          <>

          </>

          <li className="nav-item">
            <Link
              className={`nav-link ${slug === "subscription" ? "active" : ""}`}
              to="/subscription"
              onClick={handleMenuItemClick}
            >
              <i className="material-symbols-outlined">analytics</i>
              <span>Plan & Subscription</span>
            </Link>
          </li>
          <li className="nav-item">
            <Link
              className={`nav-link ${slug === "support" ? "active" : ""}`}
              to="/support"
              onClick={handleMenuItemClick}
            >
              <i className="material-symbols-outlined">support</i>
              <span>Support</span>
            </Link>
          </li>
          <li className="nav-item">
            <Link
              className={`nav-link ${slug === "help" ? "active" : ""}`}
              to="/help"
              onClick={handleMenuItemClick}
            >
              <i className="material-symbols-outlined">help</i>
              <span>Help</span>
            </Link>
          </li>
           <li className="nav-item">
            <Link
              className={`nav-link ${slug === "dish" ? "active" : ""}`}
              to="/chef-review"
              onClick={handleMenuItemClick}
            >
              <i className="material-symbols-outlined">reviews</i>
              <span>Reviews</span>
            </Link>
            <ul className="sub-menu ps-3 ">
             

             
             

              <li className="nav-item">
                <Link
                  className={`nav-link ${slug === "dish-review" ? "active" : ""
                    }`}
                  to="/dish-review"
                  onClick={handleMenuItemClick}
                >
                  <i className="material-symbols-outlined">reviews</i>
                  <span> Dish Reviews</span>
                </Link>

              </li>

               <li className="nav-item">
                <Link
                  className={`nav-link ${slug === "chef-review" ? "active" : ""
                    }`}
                  to="/chef-review"
                  onClick={handleMenuItemClick}
                >
                  <i className="material-symbols-outlined">reviews</i>
                  <span>  Chef Reviews</span>
                </Link>
              </li>
               {/* <li className="nav-item">
            <Link
              className={`nav-link ${slug === "chef-review" ? "active" : ""
                }`}
              to="/chef-review"
              onClick={handleMenuItemClick}
            >
              <i className="material-symbols-outlined">reviews</i>
              <span> Chef Reviews</span>
            </Link>
          </li> */}
            </ul>
          </li>
        
         
        </ul>
      </div>

      <ul className="nav btm">
        <li className="nav-item">
          <Link
            className={`nav-link ${slug === "settings" ? "active" : ""}`}
            to="/settings"
            onClick={handleMenuItemClick}
          >
            <span>Settings</span>
            <i className="material-symbols-outlined">settings</i>
          </Link>
        </li>
      </ul>
    </section>
  );
};

export default Sidebar;
